import React from "react";
import { TreeTable } from "primereact/treetable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { useData } from "./DataProvider";
import "../Assets/styles/newStyles/DoctorOrgsTable.css";
import ClipLoader from "react-spinners/ClipLoader";

import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";

export default function EnhancedTable() {
  const [globalFilter, setGlobalFilter] = React.useState("");
  const { rows, loading, maxLevel } = useData();

  // Base level names
  const baseLevelNames = [
    "Zones",
    "ZSM",
    "RSM",
    "ASM",
    "TM",
    "Healthcare Providers",
    "Doctors",
  ];

  // Dynamically adjust level names based on maxLevel
  const adjustedLevelNames =
    maxLevel > 0
      ? baseLevelNames.slice(baseLevelNames.length - maxLevel - 1)
      : baseLevelNames;

  // Function to flatten the tree structure
  const flattenTree = (nodes, hierarchy = [], level = 0) => {
    return nodes.flatMap((node) => {
      const { cityName, patientCount, assessmentCount, doctorCount } =
        node.data;

      // Create the hierarchy array for the current node
      const currentHierarchy = [...hierarchy];
      currentHierarchy[level] = cityName;

      // Flatten the current node
      const flattenedNode = {
        ...currentHierarchy.reduce((acc, name, idx) => {
          const columnName = adjustedLevelNames[idx] || `Level ${idx + 1}`; // Use adjusted level names
          acc[columnName] = name || "-"; // Add hierarchy level columns
          return acc;
        }, {}),
        "Patient Count": patientCount,
        "Assessment Count": assessmentCount,
        "Doctor Count": doctorCount,
      };

      // Flatten children recursively
      const children = node.children
        ? flattenTree(node.children, currentHierarchy, level + 1)
        : [];

      return [flattenedNode, ...children];
    });
  };

  const exportToExcel = () => {
    const flatData = flattenTree(rows);

    // Reorder columns to match the desired order, including "Doctor Count" and "Healthcare Providers"
    const orderedData = flatData.map((row) => ({
      ...adjustedLevelNames.reduce((acc, name) => {
        acc[name] = row[name] || "-";
        return acc;
      }, {}),
      "Doctor Count": row["Doctor Count"] || 0,
      // "Patient Count": row["Patient Count"] || 0,
      "Assessment Count": row["Assessment Count"] || 0,
    }));

    const worksheet = XLSX.utils.json_to_sheet(orderedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "TreeTable Data");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, "Export.xlsx");
  };

  return (
    <>
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <ClipLoader color={"#fd8c00"} size={30} />
        </div>
      ) : (
        <>
          {/* Export Button */}
          <div
            style={{
              marginBottom: "1rem",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <span className="p-input-icon-left">
              <i className="pi pi-search" />
              <InputText
                type="search"
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                placeholder="Search"
              />
            </span>
            <button onClick={exportToExcel} className="export-button">
              Export
            </button>
          </div>

          {/* TreeTable with sorting and filtering */}
          <TreeTable
            value={rows}
            tableStyle={{ minWidth: "50rem" }}
            globalFilterFields={[
              "cityName",
              // "patientCount",
              "assessmentCount",
              "doctorCount",
            ]}
            globalFilter={globalFilter}
          >
            <Column
              className="columnHeader"
              field="cityName"
              header="Name"
              expander
              sortable
              filter
              filterPlaceholder="Filter"
            />
            <Column
              field="doctorCount"
              header="Doctor Count"
              sortable
              filter
              filterPlaceholder="Filter"
            />
            {/* <Column
              field="patientCount"
              header="Patient Count"
              sortable
              filter
              filterPlaceholder="Filter"
            /> */}
            <Column
              field="assessmentCount"
              header="Assessment Count"
              sortable
              filter
              filterPlaceholder="Filter"
            />
          </TreeTable>
        </>
      )}
    </>
  );
}
